import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { updateMyProfile } from "@/lib/profile.functions";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, Trash2 } from "lucide-react";


export const Route = createFileRoute("/settings/profile")({
  head: () => ({ meta: [{ title: "Editar perfil — Viny Club" }] }),
  component: SettingsProfile,
});

function SettingsProfile() {
  const { profile, loading, isAuthenticated } = useCurrentUser();
  const qc = useQueryClient();
  const updateFn = useServerFn(updateMyProfile);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);


  const [form, setForm] = useState({
    display_name: "",
    username: "",
    bio: "",
    city: "",
    country: "",
    avatar_url: "",
    favorite_genres: "",
  });

  useEffect(() => {
    if (profile) {
      setForm({
        display_name: profile.display_name ?? "",
        username: profile.username,
        bio: profile.bio ?? "",
        city: profile.city ?? "",
        country: profile.country ?? "",
        avatar_url: profile.avatar_url ?? "",
        favorite_genres: profile.favorite_genres.join(", "),
      });
    }
  }, [profile]);

  const save = useMutation({
    mutationFn: () =>
      updateFn({
        data: {
          display_name: form.display_name || null,
          username: form.username,
          bio: form.bio || null,
          city: form.city || null,
          country: form.country || null,
          avatar_url: form.avatar_url || null,
          favorite_genres: form.favorite_genres
            .split(",")
            .map((g) => g.trim())
            .filter(Boolean),
        },
      }),
    onSuccess: () => {
      toast.success("Perfil atualizado");
      qc.invalidateQueries({ queryKey: ["current-profile"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isAuthenticated && !loading) {
    return <p className="text-muted-foreground">Entre para editar seu perfil.</p>;
  }
  if (loading || !profile) return <p className="text-muted-foreground">Carregando…</p>;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="space-y-4"
    >
      <Field label="Nome de exibição">
        <Input value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} />
      </Field>
      <Field label="Username (URL do perfil)">
        <Input value={form.username} onChange={(v) => setForm({ ...form, username: v })} />
      </Field>
      <Field label="Foto de perfil">
        <div className="flex items-center gap-4">
          {form.avatar_url ? (
            <img loading="lazy" decoding="async"
              src={form.avatar_url}
              alt=""
              className="h-20 w-20 rounded-full border border-border object-cover"
            />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-dashed border-border bg-surface text-xs text-muted-foreground">
              sem foto
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) {
                  toast.error("Imagem deve ter no máximo 5MB");
                  return;
                }
                try {
                  setUploading(true);
                  const { data: u } = await supabase.auth.getUser();
                  const uid = u.user?.id;
                  if (!uid) throw new Error("Não autenticado");
                  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
                  const path = `${uid}/avatar-${Date.now()}.${ext}`;
                  const { error: upErr } = await supabase.storage
                    .from("avatars")
                    .upload(path, file, { upsert: true, contentType: file.type });
                  if (upErr) throw upErr;
                  const { data: signed, error: sErr } = await supabase.storage
                    .from("avatars")
                    .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
                  if (sErr || !signed) throw sErr ?? new Error("Falha ao gerar URL");
                  const url = signed.signedUrl;
                  setForm((f) => ({ ...f, avatar_url: url }));
                  await updateFn({ data: { avatar_url: url } });
                  qc.invalidateQueries({ queryKey: ["current-profile"] });
                  qc.invalidateQueries({ queryKey: ["profile"] });
                  toast.success("Foto atualizada");
                } catch (err) {
                  toast.error((err as Error).message);
                } finally {
                  setUploading(false);
                }
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-surface/70 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {form.avatar_url ? "Trocar foto" : "Enviar foto"}
            </button>
            {form.avatar_url && (
              <button
                type="button"
                disabled={uploading}
                onClick={async () => {
                  try {
                    setUploading(true);
                    setForm((f) => ({ ...f, avatar_url: "" }));
                    await updateFn({ data: { avatar_url: null } });
                    qc.invalidateQueries({ queryKey: ["current-profile"] });
                    qc.invalidateQueries({ queryKey: ["profile"] });
                    toast.success("Foto removida");
                  } catch (err) {
                    toast.error((err as Error).message);
                  } finally {
                    setUploading(false);
                  }
                }}
                className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remover foto
              </button>
            )}
            <p className="text-xs text-muted-foreground">PNG ou JPG, até 5MB.</p>
          </div>
        </div>
      </Field>

      <Field label="Bio">
        <textarea
          rows={3}
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          className="w-full rounded-lg border border-input bg-surface p-3 text-sm"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Cidade">
          <Input value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
        </Field>
        <Field label="País">
          <Input value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
        </Field>
      </div>
      <Field label="Gêneros favoritos (separados por vírgula)">
        <Input
          value={form.favorite_genres}
          onChange={(v) => setForm({ ...form, favorite_genres: v })}
          placeholder="Jazz, Soul, MPB"
        />
      </Field>
      <button
        type="submit"
        disabled={save.isPending}
        className="inline-flex items-center gap-2 rounded-full gradient-primary px-6 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
      >
        {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Salvar alterações
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-lg border border-input bg-surface px-3 text-sm"
    />
  );
}
